import ScreenerQuestionSchema, {
  ScreenerQuestionInfo,
} from '@ax2/common/model/schemas/ScreenerQuestionSchema';
import QuestionType from '@ax2/common/model/QuestionType';
import GridQuestionSchema from '@ax2/common/model/schemas/GridQuestionSchema';
import _ from 'lodash';
import ListQuestionSchema from '@ax2/common/model/schemas/ListQuestionSchema';
import UUID from '@ax2/common/model/UUID';

interface ScreenerLogicOptions {
  continue: string[];
  terminate: string[];
}
interface ScreenerLogic {
  continueText: string | null;
  terminateText: string | null;
}

export const getScreenerLogic = (
  schema: ScreenerQuestionSchema
): ScreenerLogic => {
  if (schema.questionType === QuestionType.ContinuousSumList) {
    //TODO: We schould move this check from here;
    return getSumLogic(schema);
  } else {
    const screenerLogicOptions = getScreenerLogicOptions(schema);
    return {
      continueText:
        screenerLogicOptions && screenerLogicOptions.continue.length
          ? getLogicTextFromOptions(screenerLogicOptions.continue)
          : null,
      terminateText:
        screenerLogicOptions && screenerLogicOptions.terminate.length
          ? getLogicTextFromOptions(screenerLogicOptions.terminate)
          : null,
    };
  }
};

const getScreenerLogicOptions = (
  schema: ScreenerQuestionSchema
): ScreenerLogicOptions | null => {
  switch (schema.questionType) {
    case QuestionType.RatingList:
    case QuestionType.CheckboxGrid:
    case QuestionType.RadioButtonGrid:
    case QuestionType.RadioButtonGridRow:
      return getGridColumnsLogic(schema);
    case QuestionType.CheckboxList:
    case QuestionType.DropdownMenu:
    case QuestionType.RadioButtonList:
    case QuestionType.RankingList:
      return getListLogic(schema);
    default:
      return null;
  }
};

const getGridColumnsLogic = (
  schema: ScreenerQuestionSchema
): ScreenerLogicOptions => {
  const columns = (schema as GridQuestionSchema).columns;
  return getLogicFromOptions(schema, columns);
};

const getListLogic = (schema: ScreenerQuestionSchema): ScreenerLogicOptions => {
  const options = (schema as ListQuestionSchema).answerOptions;
  return getLogicFromOptions(schema, options);
};

const getLogicFromOptions = <T extends { id: UUID; order: number }>(
  screener: ScreenerQuestionInfo,
  options: T[]
): ScreenerLogicOptions => {
  switch (screener.screenoutStrategy) {
    case 'Match':
      return getLogicFromMatchOptions(screener.screenout, options);
    case 'NotMatch':
      return getLogicFromNotMatchOptions(screener.screenout, options);
    default:
      throw Error(
        `Unknown ScreenoutStrategy (${screener.screenoutStrategy}) was used.`
      );
  }
};

const getLogicFromMatchOptions = <T extends { id: UUID; order: number }>(
  screenout: UUID[],
  options: T[]
): ScreenerLogicOptions => {
  const termination = _.filter(
    options,
    c => _.findIndex(screenout, s => s === c.id) !== -1
  );
  const continuous = _.filter(
    options,
    c => _.findIndex(screenout, s => s === c.id) === -1
  );
  return {
    continue: _.map(continuous, c => (c.order + 1).toString()),
    terminate: _.map(termination, t => (t.order + 1).toString()),
  };
};

const getLogicFromNotMatchOptions = <T extends { id: UUID; order: number }>(
  screenout: UUID[],
  options: T[]
): ScreenerLogicOptions => {
  const continuous = _.filter(
    options,
    c => _.findIndex(screenout, s => s === c.id) !== -1
  );
  return {
    continue: _.map(continuous, c => (c.order + 1).toString()),
    terminate: [],
  };
};

const getLogicTextFromOptions = (opts: string[]): string =>
  `If option ${_.join(opts, ' or option ')} is selected`;

const getSumLogic = (schema: ScreenerQuestionSchema): ScreenerLogic => {
  const options = (schema as ListQuestionSchema).answerOptions;
  const sOption = _.find(options, o => o.id === schema.screenout[0]);
  const position = sOption ? sOption.order + 1 : null;
  const value = schema.screenoutValue;
  return position && value
    ? {
        continueText: `If choise ${position} is greater than or equal to ${value}`,
        terminateText: `If choise ${position} is less ${value}`,
      }
    : {
        continueText: null,
        terminateText: null,
      };
};
